import * as React from 'react';
import {view, dynamic, AnimatePresence, createViewConfig} from '@sail/engine';
import {cancel, warningCircle} from '@sail/icons/react/Icon';

import {tokens} from '@sail/tokens';
import {Icon, IconConfig} from '../Icon';
import {ToastLayer} from './ToastLayer';
import type {ToastProps} from './types';
import {useAnimatedLayerIntents, useControlledTimer} from './hooks';
import {Spinner, SpinnerConfig} from '../Spinner';
import {ToastButton} from './ToastButton';
import {css} from '../../css';

export const ToastConfig = createViewConfig({
  name: 'Toast',
  props: {} as ToastProps,
  defaults: {
    type: 'neutral',
    placement: 'bottom',
  },
});

/**
 * Toasts display a message to the user.
 *
 * By default each `<Toast />` is rendered in a separate isolated "toast" layer.
 *
 * Stacking of toasts can be achieved by wrapping with `<ToastLayer />`.
 */
export const Toast = ToastConfig.createView(
  ({
    type,
    isOpen,
    children,
    subviews,
    onDismiss,
    actionLabel,
    onActionClick,
    duration = 'short',
    autoDismiss = true,
    placement = 'bottom',
    ...props
  }) => {
    const animationIntents = useAnimatedLayerIntents({
      placement,
    });

    const hasIcon = type === 'error' || type === 'pending';
    const hasAction = onActionClick && actionLabel;
    const hasDismiss = type !== 'pending';

    const shouldAutoClose = type === 'neutral' && autoDismiss;

    const timer = useControlledTimer({
      duration: duration === 'short' ? 5_000 : 8_000,
      onComplete: onDismiss,
    });

    React.useEffect(() => {
      if (!shouldAutoClose) {
        return;
      }

      if (isOpen) {
        timer.start();

        return () => {
          timer.reset();
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, shouldAutoClose]);

    const handleMouseEnter = () => {
      if (shouldAutoClose) {
        timer.pause();
      }
    };

    const handleMouseLeave = () => {
      if (shouldAutoClose) {
        timer.resume();
      }
    };

    return (
      <AnimatePresence>
        {isOpen ? (
          <ToastLayer placement={placement}>
            <view.div
              role="alert"
              aria-live="polite"
              uses={[...animationIntents]}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              {...props}
            >
              {hasIcon ? (
                <view.div
                  css={{
                    stack: 'x',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    marginRight: 'small',
                    justifyContent: 'center',
                  }}
                >
                  {type === 'error' ? (
                    <Icon icon={warningCircle} />
                  ) : (
                    <Spinner />
                  )}
                </view.div>
              ) : null}

              <view.div inherits={subviews.message}>{children}</view.div>

              {hasAction ? (
                <ToastButton inherits={subviews.action} onClick={onActionClick}>
                  {actionLabel}
                </ToastButton>
              ) : null}

              {hasDismiss ? (
                <ToastButton inherits={subviews.dismiss} onClick={onDismiss}>
                  <Icon icon={cancel} size="xsmall" />
                </ToastButton>
              ) : null}
            </view.div>
          </ToastLayer>
        ) : null}
      </AnimatePresence>
    );
  },
  {
    css: {
      stack: 'x',
      width: 'fit',
      height: 'fill',
      alignItems: 'center',
      maxWidth: '576px',
      boxShadow: 'large',
      borderRadius: 'small',
      paddingLeft: 'medium',
      position: 'relative',

      font: 'body.small',

      transition: 'all 0.2s ease-in-out',
    },
    uses: [
      dynamic((props) => {
        if (props.type !== 'error') {
          return [
            css({
              color: 'onFeedback.neutral.inverted',
              backgroundColor: 'feedback.neutral.inverted',
            }),
            IconConfig.customize({
              css: {
                fill: 'onFeedback.neutral.inverted',
              },
            }),
            SpinnerConfig.customize({
              css: {
                fill: 'onFeedback.neutral.inverted',
              },
            }),
          ];
        }

        return [
          css({
            color: 'onFeedback.critical.emphasized',
            backgroundColor: 'feedback.critical.emphasized',
          }),
          IconConfig.customize({
            css: {
              fill: 'onFeedback.critical.emphasized',
            },
          }),
          SpinnerConfig.customize({
            css: {
              fill: 'onFeedback.critical.emphasized',
            },
          }),
        ];
      }),
    ],
    subviews: {
      message: {
        css: {
          width: 'fill',
          marginY: 'small',
          paddingY: 'small',
          paddingRight: 'medium',
        },
      },
      action: {
        css: {
          marginX: 'small',
          paddingX: 'small',
          paddingY: 'xsmall',
          borderRadius: 'action',

          font: 'label.medium.emphasized',
        },
      },
      dismiss: {
        css: {
          alignSelf: 'stretch',

          borderTopRightRadius: 'action',
          borderBottomRightRadius: 'action',

          borderLeft: '1px solid',
          borderLeftColor: `color-mix(in srgb, ${tokens['color.border']}, transparent 75%)`,
        },
      },
    },
  },
);
